import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "design-system/atoms/Dialog"
import { useDeleteInstagramIntegrationMutation } from "modules/settings/hooks/instagram/useDeleteInstagramIntegrationMutation"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { InstagramIntegrationsRespondeData } from "schema"
import { z } from "zod"

type InstagramIntegration = z.infer<typeof InstagramIntegrationsRespondeData>[number]

type Props = {
  integration: InstagramIntegration | null
  onOpenChange: (isOpen: boolean) => void
}

export const DeleteInstagramIntegration = ({ integration, onOpenChange }: Props) => {
  const { t } = useTranslation()
  const { mutateAsync } = useDeleteInstagramIntegrationMutation()
  const [isLoading, setIsLoading] = useState(false)

  const onCancel = () => {
    onOpenChange(false)
  }

  const onDelete = async () => {
    if (!integration) return

    setIsLoading(true)

    await mutateAsync({ id: integration.id })
      .then(() => {
        setIsLoading(false)
        onOpenChange(false)
      })
      .catch(() => {
        setIsLoading(false)
      })
  }

  return (
    <Dialog open={integration !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("settings.integrations.instagram.deleteIntegration.title")}</DialogTitle>
          <DialogDescription>
            <Trans
              i18nKey={"settings.integrations.instagram.deleteIntegration.description"}
              values={{ username: integration?.username }}
              components={{ b: <b /> }}
            />
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <DialogFooter>
            <Button variant={"secondary"} onClick={onCancel} disabled={isLoading}>
              {t("settings.integrations.instagram.deleteIntegration.actions.cancel")}
            </Button>
            <Button onClick={onDelete} variant="destructive" disabled={isLoading}>
              {t("settings.integrations.instagram.deleteIntegration.actions.delete")}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  )
}
